import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import DatePicker from "react-multi-date-picker";
import { MagnifyingGlass } from "react-loader-spinner";
import { ToastContainer } from "react-toastify";
import { contextApi } from "../context/contextApi";
import { api, handleToast } from "../services/config";
import PaginationBill from "../components/PaginationBill";

const STATUS_OPTIONS = [
  { value: "1", label: "باز" },
  { value: "2", label: "در حال بررسی" },
  { value: "3", label: "در انتظار کاربر" },
  { value: "4", label: "حل‌شده" },
  { value: "5", label: "بسته" },
];

const PRIORITY_OPTIONS = [
  { value: "1", label: "کم" },
  { value: "2", label: "معمولی" },
  { value: "3", label: "بالا" },
  { value: "4", label: "فوری" },
];

const toPersianDigits = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value).replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[digit]);
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const text = String(value);

  if (text.includes("T") || text.endsWith("Z")) {
    const date = new Date(text);
    if (!Number.isNaN(date.getTime())) {
      return toPersianDigits(
        date.toLocaleString("fa-IR", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    }
  }

  const cleaned = text.replace("T", " ").replace(/\.\d+Z?$/, "").replace("Z", "");
  const [datePart, timePart = ""] = cleaned.split(" ");
  const time = timePart.slice(0, 5);
  const formatted = time ? `${datePart} ${time}` : datePart;
  return toPersianDigits(formatted);
};

const getStatusBadgeClass = (field) => {
  const value = field && typeof field === "object" ? Number(field.value) : Number(field);
  switch (value) {
    case 1:
      return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200";
    case 2:
      return "bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200";
    case 3:
      return "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200";
    case 4:
      return "bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-200";
    case 5:
      return "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200";
    default:
      return "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200";
  }
};

const getPriorityBadgeClass = (field) => {
  const value = field && typeof field === "object" ? Number(field.value) : Number(field);
  switch (value) {
    case 1:
      return "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200";
    case 2:
      return "bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200";
    case 3:
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200";
    case 4:
      return "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200";
    default:
      return "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200";
  }
};

const padDatePart = (value) => String(value).padStart(2, "0");

const formatDateForApi = (dateObj, time) => {
  if (!dateObj?.year || !dateObj?.month || !dateObj?.day) return "";
  const year = dateObj.year;
  const month = padDatePart(dateObj.month?.number ?? dateObj.month);
  const day = padDatePart(dateObj.day);
  return `${year}-${month}-${day} ${time}`;
};

const formatDateForDisplay = (dateObj) => {
  if (!dateObj?.year || !dateObj?.month || !dateObj?.day) return "";
  const year = dateObj.year;
  const month = padDatePart(dateObj.month?.number ?? dateObj.month);
  const day = padDatePart(dateObj.day);
  return `${year}/${month}/${day}`;
};

const getFieldLabel = (field, fallbackOptions = []) => {
  if (field && typeof field === "object") {
    return field.label || "-";
  }
  if (field === 0 || field) {
    return (
      fallbackOptions.find((item) => item.value === String(field))?.label ||
      String(field)
    );
  }
  return "-";
};

const formatClosedAt = (closedAt) => formatDateTime(closedAt);

const getAuthHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  authorization: `Bearer ${localStorage.getItem("login")}`,
});

export const Support = () => {
  const context = useContext(contextApi);
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [meta, setMeta] = useState(null);
  const [pageCount, setPageCount] = useState(1);
  const [loading, setLoading] = useState(false);

  const [subject, setSubject] = useState("");
  const [ticketId, setTicketId] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState(null);

  const [isDark, setIsDark] = useState(
    () =>
      typeof window !== "undefined" && document.body.classList.contains("dark")
  );

  useEffect(() => {
    const checkDark = () => setIsDark(document.body.classList.contains("dark"));
    checkDark();
    const observer = new MutationObserver(() => {
      checkDark();
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => {
      observer.disconnect();
    };
  }, []);

  const handleClick = (buttonName) => {
    context.recordClick(buttonName);
  };

  const buildFilterParams = (filters = {}) => {
    const params = [];

    if (filters.subject) params.push(`f[subject]=${encodeURIComponent(filters.subject)}`);
    if (filters.ticketId) params.push(`f[id]=${encodeURIComponent(filters.ticketId)}`);
    if (filters.status) params.push(`f[status]=${filters.status}`);
    if (filters.priority) params.push(`f[priority]=${filters.priority}`);
    if (filters.categoryId) params.push(`f[category_id]=${filters.categoryId}`);

    const hasFromDate =
      filters.fromDate &&
      filters.fromDate.year &&
      filters.fromDate.month &&
      filters.fromDate.day;
    const hasToDate =
      filters.toDate &&
      filters.toDate.year &&
      filters.toDate.month &&
      filters.toDate.day;

    if (hasFromDate) {
      params.push(
        `f[created_at][min]=${encodeURIComponent(
          formatDateForApi(filters.fromDate, "00:00:00")
        )}`
      );
    }
    if (hasToDate) {
      params.push(
        `f[created_at][max]=${encodeURIComponent(
          formatDateForApi(filters.toDate, "23:59:59")
        )}`
      );
    }

    return params;
  };

  const fetchTickets = async (page = 1, filtersOverride = null) => {
    const activeFilters = filtersOverride ?? {
      subject: subject.trim(),
      ticketId: ticketId.trim(),
      status,
      priority,
      categoryId,
      fromDate,
      toDate,
    };

    const hasFromDate =
      activeFilters.fromDate &&
      activeFilters.fromDate.year &&
      activeFilters.fromDate.month &&
      activeFilters.fromDate.day;
    const hasToDate =
      activeFilters.toDate &&
      activeFilters.toDate.year &&
      activeFilters.toDate.month &&
      activeFilters.toDate.day;

    if ((hasFromDate && !hasToDate) || (!hasFromDate && hasToDate)) {
      handleToast("error", "بازه تاریخ را کامل انتخاب کنید");
      return;
    }

    const params = buildFilterParams(activeFilters);
    params.push(`page=${page}`);

    const url = `tickets${params.length ? `?${params.join("&")}` : ""}`;

    setLoading(true);
    try {
      const response = await api.get(url, { headers: getAuthHeaders() });
      setTickets(response.data?.data || []);
      setMeta(response.data?.meta || null);
      if (response.data?.meta?.current_page) {
        setPageCount(response.data.meta.current_page);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
      handleToast("error", "خطا در دریافت لیست تیکت‌ها");
      setTickets([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get("categories?f[type]=ticket", {
        headers: getAuthHeaders(),
      });
      setCategories(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchTickets(pageCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageCount]);

  const handleSearch = async () => {
    handleClick("جستجو در پشتیبانی کلیک شد");

    const filters = {
      subject: subject.trim(),
      ticketId: ticketId.trim(),
      status,
      priority,
      categoryId,
      fromDate,
      toDate,
    };

    const displayFilters = {};
    if (filters.subject) displayFilters.subject = filters.subject;
    if (filters.ticketId) displayFilters.ticketId = filters.ticketId;
    if (filters.status) {
      displayFilters.status = getFieldLabel(filters.status, STATUS_OPTIONS);
    }
    if (filters.priority) {
      displayFilters.priority = getFieldLabel(
        filters.priority,
        PRIORITY_OPTIONS
      );
    }
    if (filters.categoryId) {
      displayFilters.category =
        categories.find((item) => String(item.id) === String(filters.categoryId))
          ?.name || filters.categoryId;
    }
    if (filters.fromDate && filters.toDate) {
      displayFilters.fromDate = formatDateForDisplay(filters.fromDate);
      displayFilters.toDate = formatDateForDisplay(filters.toDate);
    }

    setAppliedFilters(
      Object.keys(displayFilters).length ? displayFilters : null
    );
    setPageCount(1);
    await fetchTickets(1, filters);
  };

  const handleClearFilters = async () => {
    handleClick("پاک کردن فیلترها در پشتیبانی کلیک شد");
    setSubject("");
    setTicketId("");
    setStatus("");
    setPriority("");
    setCategoryId("");
    setFromDate(null);
    setToDate(null);
    setAppliedFilters(null);
    setPageCount(1);
    await fetchTickets(1, {
      subject: "",
      ticketId: "",
      status: "",
      priority: "",
      categoryId: "",
      fromDate: null,
      toDate: null,
    });
  };

  const inputClass =
    "mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-[#0D1117] text-slate-800 dark:text-slate-100 px-3 py-2.5 text-sm outline-none";

  return (
    <div className="w-full min-h-screen bg-white dark:bg-[#0D1117]">
      <ToastContainer />
      <div className="mb-4 p-3 lg:px-6 lg:pt-6">
        <h1
          className="
            relative inline-block text-3xl font-bold mb-4
            text-[#0010c6] dark:text-white
            after:content-[''] after:block after:w-full after:h-[4px]
            after:bg-[#0010c6] after:mt-[10px] dark:after:bg-[#ffffff]
          "
        >
          پشتیبانی
        </h1>
      </div>

      <div className="flex flex-col pb-10">
        <div className="w-[95%] mx-auto flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
            فیلتر تیکت‌ها
          </h3>
          <button
            type="button"
            onClick={() => {
              handleClick("ایجاد تیکت در پشتیبانی کلیک شد");
              navigate("/support/create");
            }}
            className="text-sm bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-xl text-white"
          >
            ایجاد تیکت
          </button>
        </div>

        <div className="w-[95%] mx-auto mt-5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-[#161B22] px-4 py-5 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm block text-slate-700 dark:text-slate-200">
                موضوع
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className={inputClass}
                placeholder="جستجو بر اساس موضوع"
              />
            </div>
            <div>
              <label className="text-sm block text-slate-700 dark:text-slate-200">
                شناسه تیکت
              </label>
              <input
                type="text"
                value={ticketId}
                onChange={(e) => setTicketId(e.target.value)}
                className={inputClass}
                placeholder="مثلاً ۵"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <div>
              <label className="text-sm block text-slate-700 dark:text-slate-200">
                وضعیت
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className={inputClass}
              >
                <option value="">همه</option>
                {STATUS_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm block text-slate-700 dark:text-slate-200">
                اولویت
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className={inputClass}
              >
                <option value="">همه</option>
                {PRIORITY_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm block text-slate-700 dark:text-slate-200">
                دسته‌بندی
              </label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className={inputClass}
              >
                <option value="">همه</option>
                {categories.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 items-end">
            <div>
              <span className="text-sm block text-slate-700 dark:text-slate-200">
                از تاریخ
              </span>
              <DatePicker
                calendar={persian}
                locale={persian_fa}
                value={fromDate}
                onChange={setFromDate}
                calendarPosition="bottom-right"
                inputClass="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-[#0D1117] text-slate-800 dark:text-slate-100 px-3 py-2.5 text-sm outline-none"
                containerClassName="w-full"
              />
            </div>
            <div>
              <span className="text-sm block text-slate-700 dark:text-slate-200">
                تا تاریخ
              </span>
              <DatePicker
                calendar={persian}
                locale={persian_fa}
                value={toDate}
                onChange={setToDate}
                calendarPosition="bottom-right"
                inputClass="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-[#0D1117] text-slate-800 dark:text-slate-100 px-3 py-2.5 text-sm outline-none"
                containerClassName="w-full"
              />
            </div>
            <div className="flex justify-end items-center gap-2">
              <button
                type="button"
                className="text-sm bg-slate-600 hover:bg-slate-700 px-4 py-2.5 rounded-xl text-white disabled:opacity-60"
                onClick={handleClearFilters}
                disabled={loading}
              >
                پاک کردن
              </button>
              <button
                type="button"
                className="text-sm bg-emerald-600 hover:bg-emerald-700 px-4 py-2.5 rounded-xl text-white disabled:opacity-60"
                onClick={handleSearch}
                disabled={loading}
              >
                جستجو
              </button>
            </div>
          </div>
        </div>

        <div className="w-[95%] mx-auto md:w-full">
          <div className="px-4 sm:px-6 lg:px-8">
            {appliedFilters && (
              <div className="mt-6 mb-2 p-3 bg-sky-50 dark:bg-[#1E2329] rounded-xl border border-sky-100 dark:border-slate-700">
                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-semibold text-[#0010c6] dark:text-white">
                    فیلتر براساس:
                  </span>
                  {appliedFilters.subject && (
                    <span className="px-2 py-1 rounded bg-white dark:bg-[#161B22]">
                      موضوع: {appliedFilters.subject}
                    </span>
                  )}
                  {appliedFilters.ticketId && (
                    <span className="px-2 py-1 rounded bg-white dark:bg-[#161B22]">
                      شناسه: {appliedFilters.ticketId}
                    </span>
                  )}
                  {appliedFilters.status && (
                    <span className="px-2 py-1 rounded bg-white dark:bg-[#161B22]">
                      وضعیت: {appliedFilters.status}
                    </span>
                  )}
                  {appliedFilters.priority && (
                    <span className="px-2 py-1 rounded bg-white dark:bg-[#161B22]">
                      اولویت: {appliedFilters.priority}
                    </span>
                  )}
                  {appliedFilters.category && (
                    <span className="px-2 py-1 rounded bg-white dark:bg-[#161B22]">
                      دسته: {appliedFilters.category}
                    </span>
                  )}
                  {appliedFilters.fromDate && appliedFilters.toDate && (
                    <span className="px-2 py-1 rounded bg-white dark:bg-[#161B22]">
                      تاریخ: {appliedFilters.fromDate} تا{" "}
                      {appliedFilters.toDate}
                    </span>
                  )}
                </div>
              </div>
            )}

            {loading ? (
              <div className="flex justify-center items-center py-16">
                <MagnifyingGlass
                  visible={true}
                  height="80"
                  width="80"
                  ariaLabel="magnifying-glass-loading"
                  wrapperStyle={{}}
                  wrapperClass="magnifying-glass-wrapper"
                  glassColor="#c0efff"
                  color="#0010c6"
                />
              </div>
            ) : (
              <div className="mt-8 flow-root">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                    <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm bg-white dark:bg-[#161B22]">
                      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                        <thead className="bg-slate-100 dark:bg-[#1E2329]">
                          <tr>
                            <th className="py-4 px-3 text-center text-sm font-semibold text-gray-900 dark:text-gray-300">
                              شناسه
                            </th>
                            <th className="py-4 px-3 text-center text-sm font-semibold text-gray-900 dark:text-gray-300">
                              موضوع
                            </th>
                            <th className="py-4 px-3 text-center text-sm font-semibold text-gray-900 dark:text-gray-300">
                              وضعیت
                            </th>
                            <th className="py-4 px-3 text-center text-sm font-semibold text-gray-900 dark:text-gray-300">
                              اولویت
                            </th>
                            <th className="py-4 px-3 text-center text-sm font-semibold text-gray-900 dark:text-gray-300">
                              کاربر
                            </th>
                            <th className="py-4 px-3 text-center text-sm font-semibold text-gray-900 dark:text-gray-300">
                              تاریخ ایجاد
                            </th>
                            <th className="py-4 px-3 text-center text-sm font-semibold text-gray-900 dark:text-gray-300">
                              آخرین بروزرسانی
                            </th>
                            <th className="py-4 px-3 text-center text-sm font-semibold text-gray-900 dark:text-gray-300">
                              تاریخ بستن
                            </th>
                            <th className="py-4 px-3 text-center text-sm font-semibold text-gray-900 dark:text-gray-300">
                              عملیات
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {tickets?.length > 0 ? (
                            tickets.map((ticket, index) => (
                              <tr
                                key={ticket.id}
                                onClick={() => {
                                  handleClick("مشاهده تیکت کلیک شد");
                                  navigate(`/support/${ticket.id}`);
                                }}
                                className={`cursor-pointer hover:bg-blue-50 dark:hover:bg-[#21262D] ${
                                  index % 2 === 0
                                    ? "bg-white dark:bg-[#1E2329]"
                                    : "bg-[#F8FAFC] dark:bg-[#161B22]"
                                }`}
                              >
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-gray-200 text-center">
                                  {toPersianDigits(ticket.id)}
                                </td>
                                <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-200 text-center">
                                  {ticket.subject || "-"}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                  <span
                                    className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(
                                      ticket.status
                                    )}`}
                                  >
                                    {getFieldLabel(ticket.status, STATUS_OPTIONS)}
                                  </span>
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                  <span
                                    className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getPriorityBadgeClass(
                                      ticket.priority
                                    )}`}
                                  >
                                    {getFieldLabel(
                                      ticket.priority,
                                      PRIORITY_OPTIONS
                                    )}
                                  </span>
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-gray-200 text-center">
                                  {ticket.user?.name || "-"}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-gray-200 text-center">
                                  {formatDateTime(ticket.created_at)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-gray-200 text-center">
                                  {formatDateTime(ticket.updated_at)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-gray-200 text-center">
                                  {formatClosedAt(
                                    ticket.closed_at || ticket.closed_at
                                  )}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleClick("مشاهده تیکت کلیک شد");
                                      navigate(`/support/${ticket.id}`);
                                    }}
                                    className="text-xs bg-[#0010c6] hover:bg-blue-800 px-3 py-1.5 rounded-lg text-white"
                                  >
                                    مشاهده
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan="9"
                                className="py-6 text-center text-gray-500 dark:text-gray-400"
                              >
                                تیکتی برای نمایش وجود ندارد.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {meta && (
              <PaginationBill
                meta={meta}
                pageCount={pageCount}
                setPageCount={setPageCount}
                setLoading={setLoading}
                isDark={isDark}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
